import React from 'react';
import { FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Layout from '@/components/Layout';

const TermsPage = () => {
  return (
    <Layout>
      <div className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="terms-title">Terms & Conditions</h1>
            <p className="text-slate-600">Channel Partner Program</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>EliteRentals – Channel Partner Terms & Conditions</CardTitle>
            <CardDescription>Last Updated: January 2025</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 text-sm">
            <p className="text-slate-700">
              These Terms & Conditions govern participation in the EliteRentals Channel Partner Program.
            </p>
            <p className="text-slate-700">
              By registering on the Channel Partner Portal, the partner agrees to the following:
            </p>

            <div className="space-y-6">
              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">1. Eligibility</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>The program is open to real estate agents, brokers, and property consultants.</li>
                  <li>EliteRentals reserves the right to approve, suspend, or terminate any partner account at its discretion.</li>
                </ul>
              </div>

              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">2. Referral Eligibility Rules</h3>
                <p className="text-slate-700 mb-2">A referral will be considered valid only if all conditions below are met:</p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>The customer is new to EliteRentals (no prior completed orders).</li>
                  <li>The referral applies only to the customer's first successful order.</li>
                  <li>The rental order must be for a minimum duration of 6 months.</li>
                  <li>Referral must originate via the official referral link or QR code.</li>
                  <li>Only one channel partner can be associated with a customer.</li>
                  <li>Referral is valid only within the referral validity window defined by EliteRentals.</li>
                </ul>
              </div>

              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">3. Commission Structure</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Channel Partner earns 50% of the first-month brokerage/rent.</li>
                  <li>Commission status will be marked as Pending initially.</li>
                  <li>Commission becomes Confirmed only after:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Successful delivery of items, and</li>
                      <li>First month's rent/payment is received by EliteRentals.</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">4. Payout & Cooling Period</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Commission payouts are subject to a cooling period (typically 15–30 days).</li>
                  <li>If an order is cancelled, refunded, or disputed within this period:
                    <ul className="list-disc list-inside ml-6 mt-2 space-y-1">
                      <li>Commission may be revoked.</li>
                    </ul>
                  </li>
                  <li>EliteRentals reserves the right to withhold payouts for verification or review.</li>
                </ul>
              </div>

              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">5. Prohibited Activities</h3>
                <p className="text-slate-700 mb-2">The following will lead to immediate disqualification:</p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Self-referrals (partner referring themselves or related parties)</li>
                  <li>Manipulating referral links or systems</li>
                  <li>Creating fake customers or orders</li>
                  <li>Misrepresentation of EliteRentals services</li>
                  <li>Any fraudulent or unethical activity</li>
                </ul>
              </div>

              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">6. Termination</h3>
                <p className="text-slate-700 mb-2">EliteRentals may suspend or terminate a partner account:</p>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>Without prior notice in case of fraud or misuse</li>
                  <li>With immediate effect if terms are violated</li>
                  <li>Pending commissions in such cases may be forfeited.</li>
                </ul>
              </div>

              <div className="border-l-4 border-slate-900 pl-4">
                <h3 className="font-semibold text-slate-900 text-base mb-2">7. Final Authority</h3>
                <ul className="list-disc list-inside space-y-2 text-slate-700">
                  <li>All decisions regarding referral validity, commissions, and payouts are final and binding.</li>
                  <li>EliteRentals reserves the right to update these terms at any time.</li>
                </ul>
              </div>

              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mt-6">
                <p className="text-base text-blue-900 font-medium text-center">
                  📌 By continuing to use the Channel Partner Portal, you acknowledge and accept these Terms & Conditions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TermsPage;
